/**
 * Shape of a platform's documentation.
 *
 * A platform covers exactly ONE documentation type (PUSH / EVENT / STORIES),
 * chosen when it is created and stored on the platform as `type_id`.
 *
 * Content is addressed by variant only — the optional OS split:
 *
 *   docs: [{ variant_id, content }]
 *
 * `variant_id: null` means "applies to the whole platform" — it is the only
 * content for a platform without variants, and the shared intro shown above
 * the OS tabs for a platform with variants. Keeping it as a flat list means a
 * platform with variants and one without have the same payload and the same
 * save path.
 */

/** The three documentation types. Ids are sent to the API as-is. */
export const DOC_TYPES = [
    { id: 1, label: 'PUSH', colorScheme: 'blue' },
    { id: 2, label: 'EVENT', colorScheme: 'green' },
    { id: 3, label: 'STORIES', colorScheme: 'purple' },
];

export const DEFAULT_DOC_TYPE_ID = DOC_TYPES[0].id;

export const getDocType = (id) =>
    DOC_TYPES.find(t => t.id === Number(id)) || DOC_TYPES[0];

/** UI-side stand-in for `variant_id: null` (object keys cannot be null). */
export const SHARED_VARIANT = '_shared';

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

/** Key for the flat docs map held in component state. */
export const docKey = (variantId) => variantId || SHARED_VARIANT;

export const isEmptyContent = (html) => !html?.trim() || html === '<p></p>';

/**
 * Accepts either the new payload (`docs`) or the legacy one (`content` +
 * `variants[].content`) and always returns the new shape, so the editor keeps
 * working while the backend catches up.
 */
export const normalizePlatform = (platform) => {
    if (!platform) return null;

    const variants = (platform.variants || [])
        .map((v, i) => ({ ...v, order: v.order ?? i + 1 }))
        .sort(byOrder);

    const docs = {};
    if (platform.docs?.length) {
        platform.docs.forEach((d) => {
            docs[docKey(d.variant_id)] = d.content || '';
        });
    } else {
        // Legacy: a single body per platform / per variant.
        if (platform.content) docs[docKey(null)] = platform.content;
        variants.forEach((v) => {
            if (v.content) docs[docKey(v.id)] = v.content;
        });
    }

    return {
        ...platform,
        type_id: Number(platform.type_id) || DEFAULT_DOC_TYPE_ID,
        variants,
        docs,
    };
};

/** Flat state map -> the `docs` array the API expects. Empty cells are dropped. */
export const toDocsPayload = (docsMap) =>
    Object.entries(docsMap || {})
        .filter(([, content]) => !isEmptyContent(content))
        .map(([key, content]) => ({
            variant_id: key === SHARED_VARIANT ? null : key,
            content,
        }));
